/**
 * InventoryChart — Bar chart showing monthly inventory value
 * for the past ~12 months. Uses Recharts BarChart.
 * Tooltip shows both Inventory Value ($) and On Hand Quantity (units).
 */

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { InventoryTrendPoint } from "../utils/PowerBIApiCall";
import {
  formatCurrencyAxis as formatYAxis,
  formatCurrency,
  formatNumber as formatQuantity,
} from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

interface InventoryChartProps {
  data: InventoryTrendPoint[];
}

/** Custom tooltip showing inventory value and on-hand quantity */
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: InventoryTrendPoint }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">{label}</p>
      <p className="text-xs" style={{ color: SERIES.inventory }}>
        <span className="font-medium">Inventory Value:</span>{" "}
        <span className="tabular-nums">
          {formatCurrency(point.inventoryValue)}
        </span>
      </p>
      <p className="text-xs text-gray-600">
        <span className="font-medium">On Hand Qty:</span>{" "}
        <span className="tabular-nums">
          {formatQuantity(point.onHandQuantity)}
        </span>
      </p>
    </div>
  );
}

export function InventoryChart({ data }: InventoryChartProps) {
  if (!data || data.length === 0) {
    return null;
  }

  // Calculate average for reference line
  const avgValue =
    data.reduce((sum, d) => sum + d.inventoryValue, 0) / data.length;

  return (
    <div className="card overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          Monthly Inventory Balance
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Past {data.length} months — Inventory Value at Period End
        </p>
      </div>
      <div className="px-4 py-5">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis
              dataKey="periodNameFormatted"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              angle={-45}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={65}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={avgValue}
              stroke={SERIES.priorYearLine}
              strokeDasharray="4 4"
              strokeWidth={1}
              label={{
                value: `Avg: ${formatYAxis(avgValue)}`,
                position: "right",
                fontSize: 10,
                fill: SERIES.priorYearLine,
              }}
            />
            <Bar
              dataKey="inventoryValue"
              name="Inventory Value"
              fill={SERIES.inventory}
              radius={[3, 3, 0, 0]}
              maxBarSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
